import { frontendURL, https } from "../http";
import store from "../store";
import {
  setSkypointProviderLoading,
  setSkypointProviders,
  setActiveProvider,
  setProviderSettings,
  setProviderSettingsLoading,
} from "../store/reducers/skypointProvider";
import { getCookie, setCookie, showError } from "../utils/index";
import { getProfile } from "./profile";
import swal from "sweetalert";
import {
  beginProviderAccessSession,
  endProviderAccessSession,
  getProviderAccessAuthToken,
} from "../utils/providerAccessSession";

const getAllSkypointProvider = async (
  page,
  filter = "active",
  search = "",
  sortBy = "",
  showLoading = true,
  providerType = ""
) => {
  try {
    const strUrl = `/skypointProvider/${page}?filter=${filter}${
      search ? `&search=${search}` : ""
    }${sortBy ? `&sortBy=${sortBy}` : ""}${
      providerType ? `&providerType=${providerType}` : ""
    }`;
    if (showLoading) store.dispatch(setSkypointProviderLoading(true));
    let response = await https().get(strUrl);
    if (response?.status === 200) {
      store.dispatch(setSkypointProviders(response?.data));
    }
  } catch (error) {
    store.dispatch(setSkypointProviderLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// The server tags a validation failure it can attribute to one input with
// `field` (currently only a duplicate email). Older deployments answer with the
// message alone, so recognise that shape too rather than depending on the tag.
const errorField = (error) => {
  const data = error?.response?.data;
  if (data?.field) return data.field;
  const message = data?.message || "";
  return /email/i.test(message) && /(exist|already|taken)/i.test(message) ? "email" : null;
};

// A "this email is already a provider" error belongs on the email input the
// user has to change — a toast disappears and leaves the form looking valid.
// `onFieldError(field, message)` gets first refusal and returning true from it
// suppresses the toast; anything it doesn't claim still surfaces as one.
const reportProviderError = (error, onFieldError) => {
  const message = error?.response?.data?.message;
  const field = errorField(error);
  if (field && onFieldError?.(field, message)) return;
  showError(message, error?.response?.status);
};

const addSkypointProvider = async (data, onFieldError) => {
  try {
    let response = await https().post("/skypointProvider", data);
    if (response?.status === 200) {
      swal({
        title: `Invitation email sent successfully`,
        icon: "success",
        dangerMode: false,
      });
      getAllSkypointProvider(1);
      return response?.data;
    }
  } catch (error) {
    reportProviderError(error, onFieldError);
    return false;
  }
};

const addExternalSkypointProvider = async (data, onFieldError) => {
  try {
    let response = await https().post("/skypointProvider/external", data);
    if (response?.status === 200) {
      swal({
        title: `External provider added`,
        icon: "success",
        dangerMode: false,
      });
      getAllSkypointProvider(1);
      return response?.data;
    }
  } catch (error) {
    reportProviderError(error, onFieldError);
    return false;
  }
};

const inviteSkypointProvider = async (data) => {
  try {
    let response = await https().put("/skypointProvider/invite", data);
    if (response?.status === 200) {
      swal({
        title: `Invitation email sent successfully`,
        icon: "success",
        dangerMode: false,
      });
      getAllSkypointProvider(1);
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return false;
  }
};

const editSkypointProvider = async (data) => {
  try {
    let response = await https().put("/skypointProvider", data);
    if (response?.status === 200) {
      swal({
        title: response?.data?.message || "",
        icon: "success",
        dangerMode: false,
      });
      getAllSkypointProvider(1);
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return false;
  }
};

const deleteSkypointProvider = async (id) => {
  try {
    let response = await https().delete(`/skypointProvider/${id}`);
    if (response?.status === 200) {
      swal({
        title: response?.data?.message || "",
        icon: "success",
        dangerMode: false,
      });
      getAllSkypointProvider(1);
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const generateProviderToken = async (payload) => {
  try {
    let response = await https().post(
      "/skypointProvider/generate-token",
      payload
    );
    if (response?.status === 200) {
      const { message, token, provider } = response?.data;
      window.location.href = `${frontendURL}/provider-token?token=${token}&skypoint-token=${getCookie(
        "token"
      )}`;
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const resendProviderInvitation = async (providerId) => {
  try {
    let response = await https().post("/skypointProvider/resend-invite", {
      providerId,
    });
    if (response?.status === 200) {
      swal({
        title: "Invitation email resent successfully",
        icon: "success",
        dangerMode: false,
      });
      return true;
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return false;
  }
};

const enterProviderView = async (providerId, providerName) => {
  try {
    let response = await https().post("/skypointProvider/generate-token", {
      providerId,
    });
    if (response?.status === 200) {
      const { token, accessSessionId } = response?.data;
      // OD-1524: the previous provider's access session ends only once the
      // switch has succeeded — until then the user is still viewing it.
      const skypointToken = getProviderAccessAuthToken() || getCookie("token");
      endProviderAccessSession("switched_provider");
      beginProviderAccessSession(accessSessionId, skypointToken);
      store.dispatch(
        setActiveProvider({
          id: providerId,
          name: providerName,
          token,
        })
      );
      return true;
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return false;
  }
};

const requestProviderAccess = async (providerId) => {
  try {
    let response = await https().post("/skypointProvider/send-request-access", {
      providerId,
    });
    if (response?.status === 200) {
      swal({
        title: "Access request sent to provider",
        text: "The provider will receive an email to approve your request.",
        icon: "success",
        dangerMode: false,
      });
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return false;
  }
};

const unlinkSkypointProvider = async (providerId) => {
  try {
    let response = await https().put("/skypointProvider/unlink", {
      providerId,
    });
    if (response?.status === 200) {
      swal({
        title: "Provider removed from your list",
        icon: "success",
        dangerMode: false,
      });
      return true;
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return false;
  }
};

// Searches existing Ogiso providers by business name, contact name, or email
// and resolves to an array (empty when nothing matched). The server caps the
// result count and needs at least 3 characters for a name search; a full email
// address is still matched exactly.
const searchProviders = async (term) => {
  try {
    const response = await https().get(
      `/skypointProvider/search-user?q=${encodeURIComponent(term)}`
    );
    if (response?.status === 200) {
      const { users, user } = response.data || {};
      return users || (user ? [user] : []);
    }
  } catch (error) {
    // Matching nobody is a normal outcome of a search, not a failure — and it
    // must not raise a sweetalert here. That alert renders outside the Add
    // Provider drawer, so dismissing it registered as a click outside and the
    // drawer closed, taking the search (and anything typed) with it. Callers
    // render the "no provider found" state inline instead.
    if (error?.response?.status !== 404) {
      showError(error?.response?.data?.message, error?.response?.status);
    }
  }
  return [];
};

// GET the Provider Settings page's persisted values (defaults created
// server-side on first read — see providerSettings.service.js#getOrCreateSettings).
const getProviderSettings = async () => {
  try {
    store.dispatch(setProviderSettingsLoading(true));
    const response = await https().get("/skypointProvider/settings");
    if (response?.status === 200) {
      store.dispatch(setProviderSettings(response?.data));
      return response?.data;
    }
  } catch (error) {
    store.dispatch(setProviderSettingsLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// The settings page has no Save button — every control change PUTs the
// whole object back, same pattern as the Quarterly Report settings page.
const updateProviderSettings = async (data) => {
  try {
    store.dispatch(setProviderSettingsLoading(true));
    const response = await https().put("/skypointProvider/settings", data);
    if (response?.status === 200) {
      store.dispatch(setProviderSettings(response?.data));
      return response?.data;
    }
  } catch (error) {
    store.dispatch(setProviderSettingsLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

export {
  addSkypointProvider,
  addExternalSkypointProvider,
  getAllSkypointProvider,
  editSkypointProvider,
  deleteSkypointProvider,
  generateProviderToken,
  enterProviderView,
  inviteSkypointProvider,
  resendProviderInvitation,
  searchProviders,
  requestProviderAccess,
  unlinkSkypointProvider,
  getProviderSettings,
  updateProviderSettings,
};
